import type { TranscriptSegment } from '@ai-meeting/shared'
import type { RealtimeToken } from '@soniox/client'
import { sortBySpeechStart } from '@/utils/speakerTurnOrder'

export interface SpeakerTurn {
  /** Stable key inherited from the persisted transcript segment. */
  key: string
  /** Stable speaker identifier used for labels and colour selection. */
  speaker?: string
  /** Human-readable speaker label when one was captured. */
  speakerName?: string
  text: string
  /** Milliseconds elapsed since the meeting's shared transcript baseline. */
  timestampMs?: number
}

/**
 * Earliest timestamp across every column of the bilingual preview.
 *
 * The fallback for a meeting saved before segments carried `elapsedMs`, and
 * only that. Deriving a zero from the lines in hand is what made two machines
 * in one room disagree: each held a different earliest line, so the same
 * sentence was labelled differently on each, out by the room's leading silence.
 * A segment that knows its own offset is labelled by it instead.
 *
 * Both columns must be offset by the SAME baseline. Each column deriving its
 * own would put their elapsed times on different clocks, which is wrong on the
 * face of it — the Input pane renders those times — and makes the two sides
 * impossible to compare, which is what the shared preview window below needs.
 *
 * @param segmentGroups Source and translation segments, in any order.
 * @returns Earliest `timestamp` among segments that have text, or 0 when none do.
 */
export function resolveTimelineBaselineMs(
  segmentGroups: ReadonlyArray<readonly TranscriptSegment[]>
): number {
  let baseline: number | undefined
  for (const segments of segmentGroups) {
    for (const segment of segments) {
      if (!segment.text) continue
      if (baseline === undefined || segment.timestamp < baseline) baseline = segment.timestamp
    }
  }
  return baseline ?? 0
}

/**
 * Buckets transcript segments into speaker turns on the meeting's own clock.
 *
 * Sorted by speech time rather than persistence order, because each speaker
 * arrives on their own socket: appending in arrival order collapses an
 * interleaved A/B/A conversation into A/A/B. The sort is stable, so turns
 * sharing a timestamp keep their persisted order.
 *
 * @param segments Saved segments for one column.
 * @param baselineMs Shared offset from {@link resolveTimelineBaselineMs}.
 * @returns Turns in speech order, with `timestampMs` relative to the baseline.
 */
export function segmentsToTurns(
  segments: TranscriptSegment[],
  baselineMs: number
): SpeakerTurn[] {
  const withText = segments.filter((segment) => segment.text)
  return sortBySpeechStart(
    withText.map((segment, index) => ({
      key: segment.id ?? `seg-${index}`,
      speaker: segment.speakerId || segment.speaker || '—',
      speakerName: segment.speakerName || (segment.speakerId ? segment.speaker : undefined),
      text: segment.text,
      // The saved offset first: it is the room's clock and the same on every
      // machine. The subtraction is what a meeting saved before it gets.
      timestampMs: segment.elapsedMs ?? segment.timestamp - baselineMs,
    })),
    (turn) => turn.timestampMs
  )
}

interface NamedRealtimeToken extends RealtimeToken {
  speaker_name?: string
}

/**
 * Adapts persisted meeting turns to the token contract used by the live
 * transcript renderer. Saved turns are final by definition; the selected
 * translation status preserves the live Input/Output typography.
 */
export function turnsToRealtimeTokens(
  turns: readonly SpeakerTurn[],
  translationStatus: 'original' | 'translation'
): NamedRealtimeToken[] {
  return turns.map((turn) => ({
    text: turn.text,
    confidence: 1,
    is_final: true,
    translation_status: translationStatus,
    speaker: turn.speaker,
    speaker_name: turn.speakerName,
    start_ms: turn.timestampMs,
  }))
}

/**
 * Caps one transcript column to complete consecutive speaker runs.
 *
 * Input and Output use the same run count independently because persisted
 * batch translations can all share one timestamp. A timestamp window would
 * therefore collapse Input while accidentally rendering all of Output.
 *
 * @param turns Turns for one transcript column in display order.
 * @param maxRuns Maximum consecutive speaker runs to keep.
 * @returns The leading turns without cutting through a speaker run.
 */
export function limitToSpeakerRuns<T extends { speaker?: string }>(
  turns: readonly T[],
  maxRuns: number
): T[] {
  if (maxRuns <= 0) return []

  const limited: T[] = []
  let runs = 0
  let hasLastSpeaker = false
  let lastSpeaker: string | undefined

  for (const turn of turns) {
    if (!hasLastSpeaker || turn.speaker !== lastSpeaker) {
      if (runs >= maxRuns) break
      runs += 1
      hasLastSpeaker = true
      lastSpeaker = turn.speaker
    }
    limited.push(turn)
  }

  return limited
}
